import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';

export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const scope = this.closest('.shopify-section, dialog');

    scope?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });

    // Initial filter after theme wiring
    queueMicrotask(() => {
      this.#filterAndWire(this, this.#deriveVariantName(this), { replaceSlideshow: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  // ---------- Variant HTML arrives ----------
  #handleVariantUpdate = (event) => {
    const src = event.detail?.data?.html;
    const v = event.detail?.variant;
    if (!src) return;

    // src might be a string or a DocumentFragment/Element
    let newGallery;
    if (typeof src === 'string') {
      const tpl = document.createElement('template');
      tpl.innerHTML = src;
      newGallery = tpl.content.querySelector('media-gallery');
    } else {
      newGallery = src.querySelector?.('media-gallery') || src;
      if (!(newGallery instanceof Element)) newGallery = null;
    }
    if (!newGallery) return;

    const wanted = this.#deriveVariantName(newGallery, v?.name || v?.title);
    this.#filterAndWire(newGallery, wanted, { replaceSlideshow: true });
    this.replaceWith(newGallery);
  };

  // ---------- Core filter + rebuild ----------
  #filterAndWire(galleryEl, variantNameRaw, { replaceSlideshow } = { replaceSlideshow: false }) {
    const oldSS = galleryEl.querySelector('slideshow-component');
    if (!oldSS) return;

    // Work on a fresh clone to avoid stale internal state
    const newSS = oldSS.cloneNode(true);

    // 1) Collect slide infos
    const slides = Array.from(newSS.querySelectorAll('slideshow-slide[ref="slides[]"]'));
    if (!slides.length) return;

    const infos = slides.map((slide) => {
      const media = slide.querySelector('.product-media[data-media-id]');
      const img = slide.querySelector('img[alt]');
      return {
        slide,
        mediaId: media?.getAttribute('data-media-id') || '',
        alt: (img?.getAttribute('alt') || '').trim(),
      };
    });

    // 2) Decide which alt we want (robust)
    const allAlts = Array.from(new Set(infos.map(i => i.alt).filter(Boolean)));
    const wantedAlt = this.#resolveWantedAlt(newSS, variantNameRaw, allAlts);

    // 3) Filter to matching slides
    const keep = new Set(
      infos.filter(i => this.#norm(i.alt) === this.#norm(wantedAlt)).map(i => i.mediaId)
    );
    // If we failed to resolve (rare), keep everything rather than break
    const useAll = !wantedAlt || !keep.size;

    // Remove non-matching slides
    if (!useAll) {
      infos.forEach(({ slide, mediaId }) => {
        if (!keep.has(mediaId)) slide.remove();
      });
    }

    // 4) Re-index remaining slides, update zoom indices and timeline
    const liveSlides = Array.from(newSS.querySelectorAll('slideshow-slide[ref="slides[]"]'));
    liveSlides.forEach((s, i) => {
      s.removeAttribute('hidden');
      s.removeAttribute('reveal');
      s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
      s.style.setProperty('view-timeline-name', `--slide-${i + 1}`);
      s.querySelectorAll('[on\\:click*="/open/"]').forEach((node) => {
        const val = node.getAttribute('on:click');
        if (val) node.setAttribute('on:click', val.replace(/\/open\/\d+$/, `/open/${i}`));
      });
    });
    const count = liveSlides.length;
    const timeline = Array.from({ length: count }, (_, i) => `--slide-${i + 1}`).join(', ');
    newSS.style.setProperty('--slideshow-timeline', timeline);
    newSS.setAttribute('infinite', ''); // loop

    // 5) Thumbnails: remove non-matching and reindex
    const thumbsRow = newSS.querySelector('.slideshow-controls__thumbnails');
    if (thumbsRow) {
      const thumbBtns = Array.from(thumbsRow.querySelectorAll('[ref="thumbnails[]"]'));
      if (!useAll) {
        thumbBtns.forEach((btn) => {
          const id = btn.getAttribute('data-media-id') || '';
          if (!keep.has(id)) btn.remove();
        });
      }
      Array.from(thumbsRow.querySelectorAll('[ref="thumbnails[]"]')).forEach((btn, i) => {
        btn.setAttribute('on:click', `/select/${i}`);
        btn.dataset.index = String(i);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.setAttribute('aria-label', `Slide ${i + 1} of ${count}`);
      });
    }

    // 6) Deduplicate arrows, move counter into the first arrows bar
    const arrowsBars = Array.from(newSS.querySelectorAll('slideshow-arrows'));
    const firstBar = arrowsBars[0] || null;
    if (arrowsBars.length > 1) arrowsBars.slice(1).forEach(n => n.remove());

    const counter = newSS.querySelector('.slideshow-controls__counter');
    if (firstBar && counter && !firstBar.contains(counter)) {
      firstBar.appendChild(counter); // this *moves* it out of slideshow-controls
    }

    // 7) Swap the slideshow in the DOM
    oldSS.replaceWith(newSS);

    // 8) Snap to first slide, reset scroll, set counter to 1/N and keep it synced
    const ss = this.slideshow; // re-query after replacement
    const scroller = ss?.querySelector('[ref="scroller"]');
    if (scroller && 'scrollLeft' in scroller) scroller.scrollLeft = 0;
    try { ss?.select?.(0, undefined, { animate: false }); } catch {}

    this.#updateCounter(ss, 1, count);

    // Sync counter on slide visibility changes (works with looping)
    if (scroller) {
      const slidesNow = Array.from(ss.querySelectorAll('slideshow-slide[ref="slides[]"]'));
      const sync = () => {
        const i = slidesNow.findIndex(s => s.getAttribute('aria-hidden') === 'false');
        if (i >= 0) this.#updateCounter(ss, i + 1, slidesNow.length);
      };
      new MutationObserver(sync).observe(scroller, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
        subtree: true,
      });
    }
  }

  // ---------- Helpers ----------
  #deriveVariantName(root, explicit) {
    // 1) explicit from event
    if (explicit && explicit.trim()) return explicit.trim();

    // 2) checked radio (Online Store 2.0 pickers often use this)
    const radio = document.querySelector('input[type="radio"][data-variant-id]:checked');
    if (radio?.value || radio?.getAttribute('aria-label')) {
      return (radio.getAttribute('aria-label') || radio.value || '').trim();
    }

    // 3) <select name="id">
    const sel = document.querySelector('select[name="id"] option:checked');
    if (sel?.textContent) return sel.textContent.trim();

    // 4) URL ?variant=… sometimes contains title fragments — skip

    // 5) Fallback: use the alt of the *currently visible* slide
    const ss = root.querySelector?.('slideshow-component') || this.querySelector('slideshow-component');
    if (ss) {
      const visible = ss.querySelector('slideshow-slide[ref="slides[]"][aria-hidden="false"]') ||
                      ss.querySelector('slideshow-slide[ref="slides[]"]');
      const img = visible?.querySelector('img[alt]');
      if (img?.alt) return img.alt.trim();
    }

    return ''; // last resort (will keep all slides)
  }

  #resolveWantedAlt(slideshowClone, variantNameRaw, candidateAlts) {
    const vRaw = (variantNameRaw || '').trim();
    if (!candidateAlts.length) return vRaw;

    // Normalize both sides and try best match
    const v = this.#norm(vRaw);
    if (!v) {
      // if no variant name, default to the alt of the first visible slide in the clone
      const firstVisible = slideshowClone.querySelector('slideshow-slide[ref="slides[]"][aria-hidden="false"]')
                        || slideshowClone.querySelector('slideshow-slide[ref="slides[]"]');
      const img = firstVisible?.querySelector('img[alt]');
      return img?.alt?.trim() || '';
    }

    // Exact or contains/contained match against any known alt
    let best = '';
    for (const alt of candidateAlts) {
      const a = this.#norm(alt);
      if (a === v) return alt;                 // perfect match
      if (v.includes(a) || a.includes(v)) best = alt; // fuzzy
    }
    return best || vRaw;
  }

  #norm(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/\s*[-–—/|]\s*/g, ' ') // collapse separators
      .replace(/[^\p{Letter}\p{Number}\s]+/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #updateCounter(rootEl, current, total) {
    const counter = rootEl?.querySelector('.slideshow-controls__counter');
    if (!counter) return;
    const currentEl = counter.querySelector('span[ref="current"]');
    const slashEl = counter.querySelector('span.slash');

    if (!currentEl) {
      counter.innerHTML = `<span ref="current">${current}</span><span class="slash">/</span>${total}`;
      return;
    }
    currentEl.textContent = String(current);

    if (!slashEl) {
      currentEl.insertAdjacentHTML('afterend', `<span class="slash">/</span>${total}`);
    } else {
      // ensure total is exactly after the slash
      let node = slashEl.nextSibling;
      while (node) {
        const next = node.nextSibling;
        counter.removeChild(node);
        node = next;
      }
      slashEl.insertAdjacentText('afterend', String(total));
    }
  }

  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  // Always query the current slideshow (we replace it)
  get slideshow() {
    return this.querySelector('slideshow-component');
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
